import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Editor, { loader } from "@monaco-editor/react";
import { useAuth } from "../auth/AuthContext";
import { MARKET_API_BASE, marketFetch } from "../backendConfig";
import NavBar from "./NavBar";
import PythonChart, { seriesColor } from "./PythonChart";
import { PYTHON_PRELUDE } from "./pythonPrelude";
import { PYTHON_TEMPLATES } from "./pythonTemplates";
import AddToBacktest, { LAB_STARTERS } from "./AddToBacktest";
import { LAB_HELPERS } from "../backtest/pyLab";
import { AI_BACKTEST_SUMMARY_KEY, getItems, onEditRequest } from "../backtest/labStore";
import { fonts, pageStyles, palette } from "./pageStyles";
import { makeSampleCandles } from "../backtest/sampleCandles";
import ChartLoadingOverlay from "../Chart/ChartLoadingOverlay";
import useIsMobile from "../Chart/useIsMobile";
import { FullscreenButton, ResizeHandle, fullscreenStyle, usePanelSizes } from "./panelLayout";
import {
  activePythonFile,
  addPythonFile,
  capturePythonVersion,
  loadPythonWorkspace,
  pythonVersionDiffLines,
  removePythonFile,
  renamePythonFile,
  savePythonWorkspace,
  setActivePythonFile,
  updatePythonFile,
} from "./pythonWorkspace";

// chart helpers + param()/output(), so code that works here also works in the backtest list
const EDITOR_PRELUDE = PYTHON_PRELUDE + LAB_HELPERS;

/** Starting sizes (px) of the resizable parts; the user's own sizes are remembered in this browser. */
const PANEL_DEFAULTS = { railW: 240, asideW: 360, editorH: 340, outputH: 220, chartH: 360 };

// Monaco = the editor engine inside VS Code and Google Colab. It is downloaded in the visitor's browser from
// jsDelivr (pinned version); if that fails the page falls back to a plain text box (see monacoFailed below).
loader.config({ paths: { vs: "https://cdn.jsdelivr.net/npm/monaco-editor@0.57.0/min/vs" } });
const MONACO_TIMEOUT_MS = 20000;

const CODE_STORAGE_KEY = "lwt.pythonCode";
const PACKAGES_STORAGE_KEY = "lwt.pythonPackages";
const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1d"];

const STARTER_CODE = `# Python runs in YOUR browser, or on your Mac in the Mac app (nothing is sent to a server).
# 1) pick a symbol above and press "Load candles"  ->  the variable \`candles\` is filled
# 2) press Run (or Ctrl/Cmd + Enter)
import pandas as pd


df = pd.DataFrame(candles)
df["time"] = pd.to_datetime(df["time"], unit="s")
df["sma20"] = df["close"].rolling(20).mean()
df["range"] = df["high"] - df["low"]

print(df.tail(5).to_string(index=False))
print("bars:", len(df), "| average range:", round(df["range"].mean(), 5))
`;

function readSavedCode() {
  try {
    return window.localStorage.getItem(CODE_STORAGE_KEY) || STARTER_CODE;
  } catch {
    return STARTER_CODE;
  }
}

/** Drop Pyodide's own internal stack frames so the error shows only the user's lines. */
function tidyPythonError(text) {
  const lines = String(text || "").split("\n");
  const kept = [];
  for (let i = 0; i < lines.length; i += 1) {
    if (/File "\/lib\/python[^"]*", line \d+/.test(lines[i]) || /File "[^"]*_pyodide[^"]*"/.test(lines[i])) {
      if (i + 1 < lines.length && /^\s{4}/.test(lines[i + 1])) i += 1;
      continue;
    }
    kept.push(lines[i]);
  }
  return kept.join("\n").trim();
}

function chartOutputSummary(data) {
  const names = (data?.series || []).map((series) => series.name).filter(Boolean);
  const markers = Array.isArray(data?.markers) ? data.markers.length : 0;
  const parts = [];
  if (names.length) parts.push(`plotted ${names.join(", ")}`);
  if (markers) parts.push(`${markers} signal marker${markers === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

/** Split an assistant reply into plain text pieces and ```python code blocks. */
function splitReply(text) {
  const parts = [];
  const re = /```(?:python|py)?\s*\n([\s\S]*?)```/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ kind: "text", value: text.slice(last, m.index) });
    parts.push({ kind: "code", value: m[1].replace(/\s+$/, "") });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
}

const fieldBase = {
  minHeight: "40px",
  padding: "0 10px",
  borderRadius: "9px",
  border: `1px solid ${palette.borderStrong}`,
  background: "rgba(5, 7, 11, 0.72)",
  color: palette.text,
  fontSize: "0.875rem",
};

const s = {
  // three columns: templates | editor, chart, output | AI helper. They wrap on narrow screens.
  body: { flex: 1, display: "flex", flexWrap: "wrap", alignItems: "stretch", minHeight: 0 },
  rail: {
    flex: "1 1 220px",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    background: "linear-gradient(180deg, rgba(15, 19, 27, 0.88), rgba(8, 10, 14, 0.82))",
    backdropFilter: "blur(16px)",
    borderRight: `1px solid ${palette.border}`,
    boxShadow: "inset -1px 0 0 rgba(255,255,255,0.025)",
  },
  main: { flex: "999 1 560px", minWidth: 0, display: "flex", flexDirection: "column", background: "rgba(5, 7, 11, 0.32)" },
  aside: {
    flex: "1 1 320px",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    background: "linear-gradient(180deg, rgba(15, 19, 27, 0.86), rgba(8, 10, 14, 0.8))",
    backdropFilter: "blur(16px)",
    borderLeft: `1px solid ${palette.border}`,
    boxShadow: "inset 1px 0 0 rgba(255,255,255,0.025)",
  },
  bar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px 10px",
    padding: "10px 16px",
    borderBottom: `1px solid ${palette.border}`,
    background: "rgba(10, 13, 19, 0.72)",
    backdropFilter: "blur(16px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  },
  section: { padding: "12px 16px", display: "flex", flexDirection: "column", gap: "8px", borderBottom: `1px solid ${palette.border}` },
  groupTitle: { margin: 0, padding: "12px 16px 6px", fontFamily: fonts.display, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: palette.accentBlueSoft },
  templateBtn: {
    display: "block",
    width: "100%",
    minHeight: "36px",
    padding: "6px 16px",
    border: "none",
    background: "transparent",
    color: palette.text,
    fontSize: "0.8125rem",
    textAlign: "left",
    cursor: "pointer",
  },
  row: { display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" },
  small: { fontSize: "0.75rem", lineHeight: 1.5, color: palette.mutedText },
  field: fieldBase,
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    minHeight: "40px",
    padding: "0 16px",
    borderRadius: "9px",
    border: "none",
    background: "linear-gradient(135deg, #b8d3ff, #6ea8ff)",
    color: "#07101d",
    fontWeight: 700,
    fontSize: "0.875rem",
    cursor: "pointer",
  },
  btnGhost: {
    minHeight: "36px",
    padding: "0 12px",
    borderRadius: "9px",
    border: `1px solid ${palette.borderStrong}`,
    background: "rgba(255,255,255,0.025)",
    color: palette.text,
    fontSize: "0.8125rem",
    cursor: "pointer",
  },
  chip: {
    minHeight: "32px",
    padding: "0 12px",
    borderRadius: "10px",
    border: `1px solid ${palette.border}`,
    background: "transparent",
    color: palette.text,
    fontSize: "0.75rem",
    cursor: "pointer",
    textAlign: "left",
  },
  editor: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "320px",
    resize: "vertical",
    padding: "12px",
    border: "none",
    background: palette.editor,
    color: palette.text,
    fontFamily: fonts.code,
    fontSize: "0.8125rem",
    lineHeight: 1.65,
    tabSize: 4,
    whiteSpace: "pre",
    overflow: "auto",
  },
  output: {
    margin: 0,
    minHeight: "120px",
    maxHeight: "260px",
    overflow: "auto",
    padding: "10px 16px",
    background: palette.editor,
    borderTop: `1px solid ${palette.border}`,
    fontFamily: fonts.code,
    fontSize: "0.78rem",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  chatLog: {
    flex: 1,
    minHeight: "220px",
    overflowY: "auto",
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  bubbleUser: {
    alignSelf: "flex-end",
    maxWidth: "92%",
    background: palette.accent,
    color: palette.accentText,
    borderRadius: "6px",
    padding: "8px 12px",
    fontSize: "0.8125rem",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  },
  bubbleAi: {
    alignSelf: "flex-start",
    maxWidth: "100%",
    background: palette.background,
    borderRadius: "6px",
    padding: "8px 12px",
    fontSize: "0.8125rem",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  },
  codeBlock: {
    margin: "6px 0",
    padding: "10px",
    borderRadius: "9px",
    background: palette.editor,
    border: `1px solid ${palette.border}`,
    fontFamily: fonts.code,
    fontSize: "0.75rem",
    overflowX: "auto",
    whiteSpace: "pre",
  },
};

/** Monaco colours matching the rest of the app. */
const defineEditorTheme = (monaco) => {
  monaco.editor.defineTheme("lwt-instrument", {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "comment", foreground: "6f7c90" },
      { token: "keyword", foreground: "a497e6" },
      { token: "string", foreground: "e3c46a" },
      { token: "number", foreground: "4f94db" },
    ],
    colors: {
      "editor.background": "#0a0c10",
      "editor.foreground": "#eceef1",
      "editorLineNumber.foreground": "#46536a",
      "editorLineNumber.activeForeground": "#9097a3",
      "editor.lineHighlightBackground": "#050608",
      "editor.selectionBackground": "#17191d",
      "editorCursor.foreground": "#eceef1",
      "editorIndentGuide.background1": "#0d1015",
    },
  });
};

// Set only by the Mac app (macapp/preload.js): the secret that lets this window use the Mac's own Python.
const localBridge = typeof window !== "undefined" ? window.lwtLocal : undefined;

export default function PythonPage({ embedded = false }) {
  const { token } = useAuth();
  const [runOn, setRunOn] = useState(localBridge?.key ? "mac" : "browser"); // "browser" = Pyodide, "mac" = this Mac's Python
  const abortRef = useRef(null);
  const [chartOutput, setChartOutput] = useState(null); // what plot() / signals() drew in the last run
  const [previousCode, setPreviousCode] = useState(null); // editor contents before a template replaced them
  const [templateId, setTemplateId] = useState(null); // highlighted entry in the template list
  const narrow = useIsMobile(900); // below this the columns stack and the column drag bars hide
  const [sizes, setSize] = usePanelSizes("lwt.pythonLayout", PANEL_DEFAULTS);
  const [maximized, setMaximized] = useState(null); // "editor" | "chart" | "output" | "ai" | null
  const [pageFullscreen, setPageFullscreen] = useState(false);
  const toggleMax = (key) => setMaximized((m) => (m === key ? null : key));
  useEffect(() => {
    if (!maximized) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMaximized(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [maximized]);
  useEffect(() => {
    const onChange = () => setPageFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const togglePageFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    else document.documentElement.requestFullscreen?.().catch(() => {});
  };
  const [buildText, setBuildText] = useState("");
  const [claudeConfigured, setClaudeConfigured] = useState(null);
  useEffect(() => {
    let cancelled = false;
    marketFetch(`${MARKET_API_BASE}/ai/status`)
      .then((response) => response.json())
      .then((status) => { if (!cancelled) setClaudeConfigured(Boolean(status.configured)); })
      .catch(() => { if (!cancelled) setClaudeConfigured(false); });
    return () => { cancelled = true; };
  }, [token]);

  // ---- editor + Python worker ------------------------------------------------
  const [workspace, setWorkspace] = useState(() => {
    try { return loadPythonWorkspace(window.localStorage, readSavedCode()); }
    catch { return loadPythonWorkspace(null, readSavedCode()); }
  });
  const [code, setCode] = useState(() => activePythonFile(workspace)?.code || STARTER_CODE);
  const [newFileName, setNewFileName] = useState("");
  const [renamingFileId, setRenamingFileId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [workspaceError, setWorkspaceError] = useState("");
  const importFileRef = useRef(null);
  const [lines, setLines] = useState([]); // {kind: "out" | "err" | "info", text}
  const [running, setRunning] = useState(false);
  const [pyStatus, setPyStatus] = useState("starting"); // starting | ready | error
  const [packagesText, setPackagesText] = useState(() => {
    try {
      return window.localStorage.getItem(PACKAGES_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const [monacoReady, setMonacoReady] = useState(false);
  const [monacoFailed, setMonacoFailed] = useState(false);
  const runCodeRef = useRef(() => {});
  const workerRef = useRef(null);
  const runIdRef = useRef(0);
  const lastOutputRef = useRef("");

  const pushLine = useCallback((kind, text) => {
    setLines((prev) => [...prev.slice(-400), { kind, text }]);
  }, []);

  const startWorker = useCallback(() => {
    if (workerRef.current) workerRef.current.terminate();
    setPyStatus("starting");
    const worker = new Worker(`${process.env.PUBLIC_URL || ""}/pyodide-worker.js`);
    worker.onmessage = (event) => {
      const msg = event.data || {};
      if (msg.type === "ready") setPyStatus("ready");
      else if (msg.type === "fatal") {
        setPyStatus("error");
        pushLine("err", msg.message);
      } else if (msg.type === "chart") {
        if (msg.id === runIdRef.current) {
          setChartOutput(msg.data);
          const summary = chartOutputSummary(msg.data);
          if (summary) pushLine("info", `Python updated the chart: ${summary}.`);
        }
      } else if (msg.type === "stdout") pushLine("out", msg.text);
      else if (msg.type === "stderr") pushLine("err", msg.text);
      else if (msg.type === "status") pushLine("info", msg.text);
      else if (msg.type === "done") {
        if (msg.id !== runIdRef.current) return;
        if (msg.result !== null && msg.result !== undefined) pushLine("out", msg.result);
        setRunning(false);
      } else if (msg.type === "error") {
        if (msg.id !== runIdRef.current) return;
        pushLine("err", tidyPythonError(msg.message));
        if (/ModuleNotFoundError|No module named/.test(msg.message)) {
          pushLine("info", 'Tip: type the package name in "Extra packages (pip)" above the editor and press Run again. Only pure-Python packages work here.');
        }
        setRunning(false);
      }
    };
    worker.onerror = () => {
      setPyStatus("error");
      setRunning(false);
      pushLine("err", "The Python worker crashed. Press Run to start it again.");
    };
    workerRef.current = worker;
  }, [pushLine]);

  useEffect(() => {
    if (runOn !== "browser") {
      workerRef.current?.terminate();
      workerRef.current = null;
      setPyStatus("ready");
      return undefined;
    }
    startWorker();
    return () => workerRef.current?.terminate();
  }, [startWorker, runOn]);

  useEffect(() => {
    try {
      window.localStorage.setItem(CODE_STORAGE_KEY, code);
    } catch {
      // storage unavailable: the code simply isn't remembered
    }
  }, [code]);

  useEffect(() => {
    try {
      savePythonWorkspace(window.localStorage, workspace);
      setWorkspaceError("");
    } catch {
      setWorkspaceError("The project could not be saved in this browser. Export your files to keep a copy.");
    }
  }, [workspace]);

  useEffect(() => {
    const current = activePythonFile(workspace);
    if (current && current.code !== code) {
      setWorkspace((previous) => updatePythonFile(previous, previous.activeFileId, code));
    }
  }, [code, workspace]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PACKAGES_STORAGE_KEY, packagesText);
    } catch {
      // storage unavailable
    }
  }, [packagesText]);

  // ---- candle data -----------------------------------------------------------
  const [symbol, setSymbol] = useState("EURUSD");
  const [tf, setTf] = useState("1h");
  const [limit, setLimit] = useState(1000); // default number of candles loaded
  const [candles, setCandles] = useState([]);
  const [dataNote, setDataNote] = useState("No candles loaded yet.");
  const [dataLoading, setDataLoading] = useState(false);

  const loadCandles = useCallback(async (auto) => {
    const sym = symbol.trim().toUpperCase();
    if (!sym) return;
    setDataLoading(true);
    setDataNote("Loading (the free server can take about a minute to wake up)...");
    try {
      const url = `${MARKET_API_BASE}/candles?symbol=${encodeURIComponent(sym)}&tf=${tf}&limit=${Number(limit) || 1000}`;
      const res = await marketFetch(url);
      const data = await res.json().catch(() => null);
      if (!res.ok || !Array.isArray(data)) {
        throw new Error((data && data.detail) || `Server answered ${res.status}`);
      }
      setCandles(data);
      setDataNote(
        data.length
          ? `Loaded ${data.length} ${sym} ${tf} candles, last close ${data[data.length - 1].close}.`
          : `No candles came back for ${sym} ${tf}. The market may be closed: try a longer timeframe or more bars.`
      );
    } catch (err) {
      if (auto === true) {
        // first open and no market server: give the editor something to run on
        setCandles(makeSampleCandles({ count: Number(limit) || 1000 }));
        setDataNote(`Live data isn't reachable right now (${err.message}), so these are 1,000 sample candles (made-up, EURUSD-like, 1 hour). Press Load candles to try again.`);
      } else {
        setDataNote(`Could not load candles: ${err.message}. Keeping the current candles.`);
      }
    } finally {
      setDataLoading(false);
    }
  }, [symbol, tf, limit]);

  // load the default 1,000 candles as soon as the page opens
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    if (autoLoadedRef.current) return;
    autoLoadedRef.current = true;
    loadCandles(true);
  }, [loadCandles]);

  // ---- run / stop ------------------------------------------------------------
  const runCode = useCallback(async () => {
    if (running) return;
    const packages = packagesText
      .split(/[\s,]+/)
      .map((name) => name.trim())
      .filter((name) => /^[A-Za-z0-9][A-Za-z0-9._-]*(==[A-Za-z0-9.*+!_-]+)?$/.test(name))
      .slice(0, 12);

    if (runOn === "mac" && localBridge?.key) {
      // Run with the Mac's own Python through the local backend; output streams back line by line.
      runIdRef.current += 1;
      const myId = runIdRef.current;
      setRunning(true);
      setLines([]);
      setChartOutput(null);
      const controller = new AbortController();
      abortRef.current = controller;
      let pending = "";
      let inTraceback = false;
      const flushLine = (text) => {
        if (/^Traceback \(most recent call last\)/.test(text)) inTraceback = true;
        pushLine(inTraceback ? "err" : "out", text);
      };
      try {
        const res = await fetch(`${MARKET_API_BASE}/local/run`, {
          method: "POST",
          signal: controller.signal,
          headers: { "Content-Type": "application/json", "X-Local-Key": localBridge.key },
          body: JSON.stringify({
            code,
            files: workspace.files.map((file) => ({ name: file.name, code: file.id === workspace.activeFileId ? code : file.code })),
            candles,
            packages,
            prelude: EDITOR_PRELUDE,
            timeout: 300,
          }),
        });
        if (!res.ok || !res.body) throw new Error(`The Mac app's Python runner answered ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let newline;
          while ((newline = buffer.indexOf("\n")) >= 0) {
            const raw = buffer.slice(0, newline);
            buffer = buffer.slice(newline + 1);
            if (!raw.trim()) continue;
            let event;
            try {
              event = JSON.parse(raw);
            } catch {
              continue;
            }
            if (event.t === "out") {
              pending += event.text;
              const parts = pending.split("\n");
              pending = parts.pop();
              parts.forEach(flushLine);
            } else if (event.t === "chart") {
              setChartOutput(event.data);
              const summary = chartOutputSummary(event.data);
              if (summary) pushLine("info", `Python updated the chart: ${summary}.`);
            } else if (event.t === "exit") {
              if (pending) {
                flushLine(pending);
                pending = "";
              }
              if (event.code !== 0) pushLine("info", `Finished with exit code ${event.code}.`);
            }
          }
        }
      } catch (err) {
        if (err?.name !== "AbortError") pushLine("err", err?.message || "Could not reach the Mac app's Python runner.");
      } finally {
        if (pending) flushLine(pending);
        if (runIdRef.current === myId) setRunning(false);
        abortRef.current = null;
      }
      return;
    }

    if (!workerRef.current) return;
    runIdRef.current += 1;
    setRunning(true);
    setLines([]);
    setChartOutput(null);
    workerRef.current.postMessage({
      type: "run",
      id: runIdRef.current,
      code,
      filename: workspace.files.find((file) => file.id === workspace.activeFileId)?.name || "main.py",
      projectFiles: workspace.files.map((file) => ({ name: file.name, code: file.id === workspace.activeFileId ? code : file.code })),
      candles,
      packages,
      prelude: EDITOR_PRELUDE,
    });
  }, [code, candles, running, packagesText, runOn, pushLine, workspace]);

  const stopCode = useCallback(() => {
    if (runOn === "mac") {
      abortRef.current?.abort(); // the backend kills the Python process when the connection closes
      runIdRef.current += 1;
      setRunning(false);
      pushLine("err", "Stopped.");
      return;
    }
    // The only way to interrupt Python that never ends is to throw the worker away and start a fresh one.
    runIdRef.current += 1;
    setRunning(false);
    pushLine("err", "Stopped. (Python restarts; packages reload on the next run.)");
    startWorker();
  }, [pushLine, startWorker, runOn]);

  const outputText = useMemo(() => lines.filter((l) => l.kind !== "info").map((l) => l.text).join("\n"), [lines]);
  useEffect(() => {
    lastOutputRef.current = outputText;
  }, [outputText]);

  const handleEditorKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      runCode();
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      const el = e.target;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = code.slice(0, start) + "    " + code.slice(end);
      setCode(next);
      requestAnimationFrame(() => {
        el.selectionStart = start + 4;
        el.selectionEnd = start + 4;
      });
    }
  };

  runCodeRef.current = runCode;

  useEffect(() => {
    if (monacoReady) return undefined;
    const timer = setTimeout(() => setMonacoFailed(true), MONACO_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [monacoReady]);

  const handleMonacoMount = (editor, monaco) => {
    setMonacoReady(true);
    setMonacoFailed(false);
    // Ctrl/Cmd + Enter runs the code (same shortcut as the plain text box)
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => runCodeRef.current());
  };

  // ---- Claude helper ---------------------------------------------------------
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [includeCode, setIncludeCode] = useState(false);
  const [includeOutput, setIncludeOutput] = useState(false);
  const [includeBacktest, setIncludeBacktest] = useState(false);
  const [includeData, setIncludeData] = useState(false);
  const [backtestSummary, setBacktestSummary] = useState(() => {
    try {
      const saved = window.localStorage.getItem(AI_BACKTEST_SUMMARY_KEY) || "";
      return saved.length <= 6000 ? saved : "";
    } catch {
      return "";
    }
  });
  const chatEndRef = useRef(null);

  // keep the newest message in view by scrolling the chat box only (scrollIntoView would also
  // scroll the whole page, which made phones jump to the bottom when the page opened)
  useEffect(() => {
    const box = chatEndRef.current?.parentElement;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages, chatLoading]);

  const sendChat = async (override, share = {}) => {
    const text = (typeof override === "string" ? override : chatInput).trim();
    if (!text || chatLoading) return;
    const shareCurrentCode = share.code ?? includeCode;
    const shareCurrentOutput = share.output ?? includeOutput;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setChatInput("");
    setChatLoading(true);
    setChatError(null);
    try {
      const res = await fetch(`${MARKET_API_BASE}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: next,
          provider: "anthropic",
          mode: "python",
          runtime: runOn,
          share_code: shareCurrentCode,
          share_output: shareCurrentOutput,
          share_backtest: includeBacktest && Boolean(backtestSummary),
          ...(shareCurrentCode ? { code: code.slice(0, 30000) } : {}),
          ...(shareCurrentOutput ? { output: lastOutputRef.current.slice(-9000) || null } : {}),
          ...(includeBacktest && backtestSummary ? { backtest_summary: backtestSummary } : {}),
          context:
            includeData && candles.length
              ? { symbol: symbol.trim().toUpperCase(), timeframe: tf, candles: candles.slice(-120) }
              : null,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = payload?.detail;
        throw new Error(
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d?.msg || d).join("; ")
              : res.statusText
        );
      }
      setMessages([...next, { role: "assistant", content: payload.message }]);
    } catch (err) {
      setChatError(err?.message || "Request failed");
    } finally {
      setChatLoading(false);
    }
  };

  // "Edit code" / "+ Code a strategy" in the backtest list opens the item here
  const [labLink, setLabLink] = useState(null); // {id|null, kind, name}
  const codeRef = useRef(code);
  codeRef.current = code;
  useEffect(
    () =>
      onEditRequest((id) => {
        let next = null;
        if (id.startsWith("new:")) {
          const kind = id.slice(4) === "indicator" ? "indicator" : "strategy";
          next = LAB_STARTERS[kind];
          setLabLink({ id: null, kind, name: "" });
        } else {
          const item = getItems().find((it) => it.id === id);
          if (!item) return;
          next = item.code;
          setPackagesText(item.packages.join(", "));
          setLabLink({ id: item.id, kind: item.kind, name: item.name });
        }
        if (codeRef.current.trim() !== next.trim()) setPreviousCode(codeRef.current); // nothing is lost
        setCode(next);
        setTemplateId(null);
        setChartOutput(null);
        setLines([]);
      }),
    []
  );

  const loadTemplate = (id) => {
    const template = PYTHON_TEMPLATES.find((t) => t.id === id);
    if (!template) return;
    setTemplateId(id);
    if (code.trim() !== template.code.trim()) setPreviousCode(code); // keep the old code so nothing is lost
    setCode(template.code);
    setChartOutput(null);
    setLines([]);
    if (candles.length === 0) setDataNote('Template loaded. Press "Load candles", then Run.');
  };

  const hasError = !running && lines.some((l) => l.kind === "err" && /Error|Exception|Traceback/.test(l.text));

  const buildIndicator = () => {
    const wish = buildText.trim();
    if (!wish) return;
    setBuildText("");
    sendChat(
      "Build me this in the editor, using plot() and signals() so it shows on the chart: " +
        wish +
        "\nGive the complete script in one python block, with the settings as UPPER_CASE numbers at the top."
    );
  };

  const statusLabel =
    runOn === "mac" ? "Python on this Mac" : pyStatus === "ready" ? "Python ready" : pyStatus === "error" ? "Python failed to load" : "Python is loading (first time about 10 s)...";

  const templateList = (kind) =>
    PYTHON_TEMPLATES.filter((t) => t.kind === kind).map((t) => (
      <button
        key={t.id}
        type="button"
        title={t.description}
        aria-pressed={templateId === t.id}
        onClick={() => loadTemplate(t.id)}
        style={{ ...s.templateBtn, background: templateId === t.id ? palette.selected : "transparent" }}
      >
        {t.name}
      </button>
    ));

  const activeFile = activePythonFile(workspace);
  const selectedVersion = activeFile?.versions.find((version) => version.id === selectedVersionId) || null;
  const currentVersionDiff = selectedVersion ? pythonVersionDiffLines(selectedVersion.code, code) : null;

  const createFile = (requestedName = newFileName) => {
    try {
      const current = activeFile ? updatePythonFile(workspace, activeFile.id, code) : workspace;
      let name = requestedName.trim();
      if (!name) {
        let index = 1;
        const existingNames = new Set(current.files.map((file) => file.name.toLowerCase()));
        while (existingNames.has(`script-${index}.py`)) index += 1;
        name = `script-${index}.py`;
      }
      const result = addPythonFile(current, name);
      setWorkspace(result.workspace);
      setCode(result.file.code);
      setNewFileName("");
      setSelectedVersionId("");
      setComparisonOpen(false);
      setWorkspaceError("");
    } catch (error) {
      setWorkspaceError(error.message || "Could not add that Python file.");
    }
  };

  const selectFile = (fileId) => {
    const current = activeFile ? updatePythonFile(workspace, activeFile.id, code) : workspace;
    const nextFile = current.files.find((file) => file.id === fileId);
    if (!nextFile) return;
    setWorkspace(setActivePythonFile(current, fileId));
    setCode(nextFile.code);
    setSelectedVersionId("");
    setComparisonOpen(false);
  };

  const saveVersion = () => {
    if (!activeFile) return;
    const current = updatePythonFile(workspace, activeFile.id, code);
    const result = capturePythonVersion(current, activeFile.id, code);
    if (!result.version) {
      setWorkspaceError("This version is already saved.");
      return;
    }
    setWorkspace(result.workspace);
    setSelectedVersionId(result.version.id);
    setComparisonOpen(false);
    setWorkspaceError("");
  };

  const applyRename = (fileId) => {
    try {
      const current = activeFile ? updatePythonFile(workspace, activeFile.id, code) : workspace;
      setWorkspace(renamePythonFile(current, fileId, renameDraft));
      setRenamingFileId(null);
      setWorkspaceError("");
    } catch (error) {
      setWorkspaceError(error.message || "Could not rename that file.");
    }
  };

  const deleteFile = (fileId) => {
    try {
      const current = activeFile ? updatePythonFile(workspace, activeFile.id, code) : workspace;
      const next = removePythonFile(current, fileId);
      setWorkspace(next);
      if (fileId === workspace.activeFileId) setCode(next.files.find((file) => file.id === next.activeFileId)?.code || "");
      setSelectedVersionId("");
      setComparisonOpen(false);
      setWorkspaceError("");
    } catch (error) {
      setWorkspaceError(error.message || "Could not remove that file.");
    }
  };

  const importPythonFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      if (file.size > 30_000) throw new Error("Python files must be 30 KB or smaller.");
      const source = await file.text();
      createFile(file.name, source);
    } catch (error) {
      setWorkspaceError(error.message || "Could not import that Python file.");
    } finally {
      event.target.value = "";
    }
  };

  const exportPythonFile = () => {
    if (!activeFile) return;
    const url = URL.createObjectURL(new Blob([code], { type: "text/x-python;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = activeFile.name;
    link.click();
    URL.revokeObjectURL(url);
  };

  const aiName = "Claude";
  const statusColor = pyStatus === "error" && runOn !== "mac" ? palette.down : runOn === "mac" || pyStatus === "ready" ? palette.up : palette.highlight;

  return (
    <div
      style={
        embedded
          ? { ...pageStyles.page, height: "100%", minHeight: 0, flex: 1, overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch" }
          : { ...pageStyles.page, ...(narrow ? {} : { height: "100vh" }), overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch" }
      }
    >
      {!embedded && <NavBar />}

      {/* data bar: which candles the code runs on */}
      <div style={s.bar}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 40, paddingRight: 14, marginRight: 2, borderRight: `1px solid ${palette.border}` }}>
          <span aria-hidden="true" style={{ display: "grid", placeItems: "center", width: 34, height: 34, border: "1px solid rgba(110,168,255,0.26)", borderRadius: 10, color: palette.accentBlueSoft, background: "linear-gradient(145deg, rgba(110,168,255,0.2), rgba(110,168,255,0.06))", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)" }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m8 7-4 5 4 5M16 7l4 5-4 5M14 5l-4 14" /></svg>
          </span>
          <span style={{ display: "grid", gap: 2 }}>
            <strong style={{ color: palette.text, fontFamily: fonts.display, fontSize: 12, fontWeight: 680, letterSpacing: "0.025em" }}>Python Studio</strong>
            <span style={{ color: palette.dimText, fontFamily: fonts.code, fontSize: 9 }}>{activeFile?.name || "main.py"}</span>
          </span>
        </div>
        <label style={{ ...s.row, ...s.small, gap: "8px" }}>
          Symbol
          <input
            style={{ ...s.field, width: "120px", fontWeight: 600 }}
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder="EURUSD"
            aria-label="Symbol"
          />
        </label>
        <div role="group" aria-label="Timeframe" style={{ display: "flex", gap: "2px", padding: 3, border: `1px solid ${palette.border}`, borderRadius: 10, background: "rgba(5,7,11,0.65)" }}>
          {TIMEFRAMES.map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={tf === t}
              onClick={() => setTf(t)}
              style={{
                minHeight: "40px",
                minWidth: "40px",
                padding: "0 9px",
                border: `1px solid ${tf === t ? "rgba(110,168,255,0.24)" : "transparent"}`,
                borderRadius: "7px",
                background: tf === t ? "linear-gradient(135deg, rgba(110,168,255,0.24), rgba(110,168,255,0.11))" : "transparent",
                color: tf === t ? palette.accentBlueSoft : palette.mutedText,
                fontFamily: fonts.code,
                fontSize: "0.72rem",
                fontWeight: tf === t ? 700 : 550,
                fontStretch: "87%",
                cursor: "pointer",
              }}
            >
              {t}
            </button>
          ))}
        </div>
        <label style={{ ...s.row, ...s.small, gap: "8px" }}>
          Bars
          <input
            style={{ ...s.field, width: "84px" }}
            type="number"
            min="10"
            max="5000"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            aria-label="Number of bars"
          />
        </label>
        <button
          type="button"
          style={{ ...s.btnGhost, minHeight: "40px", borderColor: palette.text, fontWeight: 600 }}
          onClick={loadCandles}
          disabled={dataLoading}
        >
          {dataLoading ? "Loading..." : "Load candles"}
        </button>
        <span style={{ ...s.small, flex: "1 1 260px" }}>
          {dataNote} Available in your code as the list <code>candles</code> (time, open, high, low, close, volume).
        </span>
        <span role="status" style={{ ...s.row, ...s.small, gap: "8px" }}>
          <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, background: statusColor }} />
          {statusLabel}
        </span>
        <FullscreenButton active={pageFullscreen} onToggle={togglePageFullscreen} label="whole page" />
      </div>

      <div style={{ ...s.body, flex: "0 0 auto", flexWrap: narrow ? "wrap" : "nowrap", overflowY: "visible", ...(narrow ? { alignContent: "flex-start" } : {}) }}>
        {/* ---------------- left: ready-made templates ---------------- */}
        <nav
          style={{ ...s.rail, ...(narrow ? {} : { flex: `0 0 ${sizes.railW}px` }) }}
          aria-label="Ready-made indicators and strategies"
        >
          {narrow ? (
            <div style={{ padding: "10px 16px" }}>
              <details style={{ marginBottom: 10, paddingBottom: 9, borderBottom: `1px solid ${palette.border}` }}>
                <summary style={{ cursor: "pointer", color: palette.text, fontSize: 12, padding: "6px 0" }}>Project files ({workspace.files.length})</summary>
                <div style={{ display: "flex", gap: 5, marginTop: 6 }}>
                  <input value={newFileName} onChange={(event) => setNewFileName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") createFile(); }} placeholder="name.py" aria-label="New Python file name" style={{ ...s.field, minWidth: 0, flex: 1 }} />
                  <button type="button" style={s.btnGhost} onClick={() => createFile()}>New</button>
                </div>
                <input ref={importFileRef} type="file" accept=".py,text/x-python,text/plain" onChange={importPythonFile} style={{ display: "none" }} />
                <div style={{ display: "flex", gap: 6, margin: "6px 0" }}>
                  <button type="button" style={{ ...s.btnGhost, flex: 1 }} onClick={() => importFileRef.current?.click()}>Import .py</button>
                  <button type="button" style={{ ...s.btnGhost, flex: 1 }} onClick={exportPythonFile}>Export active</button>
                </div>
                {workspace.files.map((file) => <div key={file.id} style={{ display: "flex", gap: 4, marginTop: 3 }}>
                  <button type="button" onClick={() => selectFile(file.id)} style={{ ...s.templateBtn, textAlign: "left", flex: 1, background: activeFile?.id === file.id ? palette.selected : "transparent" }}>{file.name}</button>
                  <button type="button" aria-label={`Rename ${file.name}`} onClick={() => { const name = window.prompt("New file name", file.name); if (name) { try { setWorkspace(renamePythonFile(workspace, file.id, name)); } catch (error) { setWorkspaceError(error.message); } } }} style={s.btnGhost}>✎</button>
                  <button type="button" aria-label={`Delete ${file.name}`} disabled={workspace.files.length <= 1} onClick={() => deleteFile(file.id)} style={s.btnGhost}>×</button>
                </div>)}
                <div style={{ display: "flex", gap: 5, marginTop: 7 }}>
                  <select value={selectedVersionId} onChange={(event) => setSelectedVersionId(event.target.value)} aria-label="Saved Python versions" style={{ ...s.field, minWidth: 0, flex: 1 }}>
                    <option value="">Version history</option>
                    {(activeFile?.versions || []).map((version) => <option key={version.id} value={version.id}>{version.label} · {new Date(version.createdAt).toLocaleString()}</option>)}
                  </select>
                  <button type="button" disabled={!selectedVersion} onClick={() => setComparisonOpen(true)} style={s.btnGhost}>Compare</button>
                </div>
                {selectedVersion && <button type="button" onClick={() => { setCode(selectedVersion.code); setComparisonOpen(false); }} style={{ ...s.btnGhost, width: "100%", marginTop: 5 }}>Restore version</button>}
                {workspaceError && <div role="status" style={{ color: palette.danger, fontSize: 11, marginTop: 6 }}>{workspaceError}</div>}
              </details>
              <select
                style={{ ...s.field, width: "100%" }}
                value={templateId || ""}
                onChange={(e) => e.target.value && loadTemplate(e.target.value)}
                aria-label="Ready-made indicators and strategies"
              >
                <option value="">Ready-made indicators and strategies</option>
                <optgroup label="Indicators">
                  {PYTHON_TEMPLATES.filter((t) => t.kind === "indicator").map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
                <optgroup label="Strategies with backtest">
                  {PYTHON_TEMPLATES.filter((t) => t.kind === "strategy").map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          ) : (
            <>
              <section aria-label="Python project files" style={{ padding: "12px", borderBottom: `1px solid ${palette.border}` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 7 }}>
                  <h2 style={{ ...s.groupTitle, margin: 0 }}>Project files</h2>
                  <span title="Saved to this browser only" style={{ color: palette.dimText, fontSize: 9 }}>LOCAL</span>
                </div>
                <div style={{ display: "flex", gap: 5 }}>
                  <input
                    style={{ ...s.field, flex: 1, minWidth: 0, minHeight: 32, padding: "0 7px", fontSize: 11 }}
                    value={newFileName}
                    onChange={(event) => setNewFileName(event.target.value)}
                    onKeyDown={(event) => { if (event.key === "Enter") createFile(); }}
                    placeholder="name.py"
                    aria-label="New Python file name"
                  />
                  <button type="button" style={{ ...s.btnGhost, minHeight: 32, padding: "0 8px" }} onClick={() => createFile()}>New</button>
                </div>
                <input ref={importFileRef} type="file" accept=".py,text/x-python,text/plain" onChange={importPythonFile} style={{ display: "none" }} />
                <div style={{ display: "flex", gap: 6, margin: "6px 0 8px" }}>
                  <button type="button" style={{ ...s.btnGhost, minHeight: 29, flex: 1, padding: "0 7px", fontSize: 10 }} onClick={() => importFileRef.current?.click()}>Import .py</button>
                  <button type="button" style={{ ...s.btnGhost, minHeight: 29, flex: 1, padding: "0 7px", fontSize: 10 }} onClick={exportPythonFile} title={`Download ${activeFile?.name || "Python file"}`}>Export active</button>
                </div>
                <div style={{ display: "grid", gap: 3 }}>
                  {workspace.files.map((file) => (
                    <div key={file.id} style={{ display: "flex", alignItems: "center", gap: 3, minWidth: 0 }}>
                      {renamingFileId === file.id ? (
                        <>
                          <input autoFocus value={renameDraft} onChange={(event) => setRenameDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") applyRename(file.id); if (event.key === "Escape") setRenamingFileId(null); }} aria-label={`Rename ${file.name}`} style={{ ...s.field, width: 0, minWidth: 0, flex: 1, minHeight: 30, padding: "0 6px", fontSize: 10 }} />
                          <button type="button" title="Save file name" onClick={() => applyRename(file.id)} style={{ ...s.btnGhost, minHeight: 28, padding: "0 6px", fontSize: 9 }}>OK</button>
                        </>
                      ) : (
                        <>
                          <button type="button" aria-current={activeFile?.id === file.id ? "true" : undefined} onClick={() => selectFile(file.id)} title={file.name} style={{ ...s.templateBtn, textAlign: "left", flex: 1, minWidth: 0, padding: "6px 8px", color: activeFile?.id === file.id ? palette.text : palette.mutedText, background: activeFile?.id === file.id ? palette.selected : "transparent", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</button>
                          <button type="button" title={`Rename ${file.name}`} aria-label={`Rename ${file.name}`} onClick={() => { setRenamingFileId(file.id); setRenameDraft(file.name); }} style={{ ...s.btnGhost, minHeight: 28, padding: "0 6px", fontSize: 10 }}>✎</button>
                          <button type="button" title={`Delete ${file.name}`} aria-label={`Delete ${file.name}`} disabled={workspace.files.length <= 1} onClick={() => deleteFile(file.id)} style={{ ...s.btnGhost, minHeight: 28, padding: "0 6px", fontSize: 10, color: palette.danger, opacity: workspace.files.length <= 1 ? 0.4 : 1 }}>×</button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 5, marginTop: 8 }}>
                  <select value={selectedVersionId} onChange={(event) => setSelectedVersionId(event.target.value)} aria-label="Saved Python versions" style={{ ...s.field, minWidth: 0, flex: 1, minHeight: 30, padding: "0 5px", fontSize: 10 }}>
                    <option value="">Version history</option>
                    {(activeFile?.versions || []).map((version) => <option key={version.id} value={version.id}>{version.label} · {new Date(version.createdAt).toLocaleString()}</option>)}
                  </select>
                  <button type="button" disabled={!selectedVersion} onClick={() => setComparisonOpen(true)} style={{ ...s.btnGhost, minHeight: 30, padding: "0 7px", fontSize: 10 }}>Compare</button>
                </div>
                {selectedVersion && <button type="button" onClick={() => { setCode(selectedVersion.code); setComparisonOpen(false); }} style={{ ...s.btnGhost, width: "100%", minHeight: 29, marginTop: 5, padding: "0 8px", fontSize: 10 }}>Restore selected version</button>}
                {workspaceError && <div role="status" style={{ color: palette.danger, fontSize: 10, lineHeight: 1.45, marginTop: 7 }}>{workspaceError}</div>}
              </section>
              <h2 style={s.groupTitle}>Indicators</h2>
              {templateList("indicator")}
              <h2 style={s.groupTitle}>Strategies with backtest</h2>
              {templateList("strategy")}
            </>
          )}
          {previousCode !== null && (
            <div style={{ padding: "12px 16px" }}>
              <button
                type="button"
                style={{ ...s.btnGhost, width: "100%" }}
                onClick={() => {
                  setCode(previousCode);
                  setPreviousCode(null);
                  setTemplateId(null);
                }}
              >
                Restore my previous code
              </button>
            </div>
          )}
          <p style={{ ...s.small, margin: "auto 0 0", padding: "12px 16px", borderTop: `1px solid ${palette.border}`, display: narrow ? "none" : "block" }}>
            Strategy results are for learning. No fees or slippage, and past results don't predict future ones.
          </p>
        </nav>
        {!narrow && (
          <ResizeHandle
            orientation="vertical"
            label="Resize templates panel"
            value={sizes.railW}
            min={160}
            max={420}
            onChange={(v) => setSize("railW", v)}
            onReset={() => setSize("railW", PANEL_DEFAULTS.railW)}
          />
        )}

        {/* ---------------- middle: editor, chart, output ---------------- */}
        <main style={s.main}>
          <section
            aria-label="Code editor"
            style={maximized === "editor" ? fullscreenStyle : { display: "flex", flexDirection: "column", flex: "0 0 auto" }}
          >
          <div style={{ ...s.bar, padding: "8px 12px" }}>
            {running ? (
              <button type="button" style={{ ...s.btn, background: palette.down }} onClick={stopCode}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="5" y="5" width="14" height="14" /></svg>
                Stop
              </button>
            ) : (
              <button
                type="button"
                style={{ ...s.btn, ...(pyStatus === "error" && runOn !== "mac" ? pageStyles.buttonDisabled : {}) }}
                onClick={runCode}
                disabled={pyStatus === "error" && runOn !== "mac"}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 4l14 8-14 8z" /></svg>
                Run
                <span style={{ fontWeight: 500, fontSize: "0.75rem", opacity: 0.7 }}>Ctrl/Cmd + Enter</span>
              </button>
            )}
            <button
              type="button"
              style={{ ...s.btnGhost, border: "none" }}
              onClick={() => {
                setCode(STARTER_CODE);
                setTemplateId(null);
              }}
            >
              Reset example
            </button>
            <span title="Active project file" style={{ ...s.small, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeFile?.name || "main.py"}</span>
            <button type="button" style={s.btnGhost} onClick={saveVersion} disabled={!activeFile || activeFile.versions[0]?.code === code}>Save version</button>
            <input
              style={{ ...s.field, flex: "1 1 220px", minWidth: 0 }}
              value={packagesText}
              onChange={(e) => setPackagesText(e.target.value)}
              placeholder="Extra packages (pip), e.g. pandas-ta, arch"
              aria-label="Extra packages from PyPI"
              title={runOn === "mac" ? "Installed with pip on this Mac" : "Pure-Python packages only in the browser"}
              spellCheck={false}
            />
            {localBridge?.key && (
              <div role="group" aria-label="Where to run" style={{ display: "flex", padding: 2, gap: 2, border: `1px solid ${palette.border}`, borderRadius: 4 }}>
                {[
                  ["mac", "This Mac"],
                  ["browser", "Browser (limited)"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={runOn === id}
                    onClick={() => setRunOn(id)}
                    style={{
                      minHeight: "34px",
                      padding: "0 10px",
                      border: "none",
                      borderRadius: 3,
                      background: runOn === id ? palette.selected : "transparent",
                      color: palette.text,
                      fontSize: "0.75rem",
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <FullscreenButton active={maximized === "editor"} onToggle={() => toggleMax("editor")} label="code editor" />
          </div>
          <AddToBacktest code={code} packagesText={packagesText} candles={candles} link={labLink} setLink={setLabLink} />

          {comparisonOpen && selectedVersion && currentVersionDiff && (
            <section aria-label="Python version comparison" style={{ margin: "8px 12px", padding: 10, border: `1px solid ${palette.border}`, borderRadius: 6, background: palette.glass }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <strong style={{ fontSize: 11, marginRight: "auto" }}>Compare {selectedVersion.label} with current {activeFile?.name}</strong>
                <button type="button" style={s.btnGhost} onClick={() => setComparisonOpen(false)}>Close</button>
              </div>
              {currentVersionDiff.same ? <div style={{ color: palette.mutedText, fontSize: 11 }}>No changes from this saved version.</div> : (
                <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "1fr 1fr", gap: 8 }}>
                  <div><div style={{ color: palette.down, fontSize: 10, marginBottom: 4 }}>SAVED VERSION · {currentVersionDiff.removed.length} changed lines</div><pre style={{ ...s.output, maxHeight: 170, color: palette.down, whiteSpace: "pre-wrap" }}>{currentVersionDiff.removed.map((line) => `− ${line}`).join("\n") || "(no removed lines)"}</pre></div>
                  <div><div style={{ color: palette.up, fontSize: 10, marginBottom: 4 }}>CURRENT FILE · {currentVersionDiff.added.length} changed lines</div><pre style={{ ...s.output, maxHeight: 170, color: palette.up, whiteSpace: "pre-wrap" }}>{currentVersionDiff.added.map((line) => `+ ${line}`).join("\n") || "(no added lines)"}</pre></div>
                </div>
              )}
            </section>
          )}

          <div style={maximized === "editor" ? { flex: 1, minHeight: 0 } : { height: sizes.editorH }}>
          {monacoFailed && !monacoReady ? (
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={handleEditorKeyDown}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              aria-label="Python code"
              style={{ ...s.editor, height: "100%", minHeight: 0, resize: "none" }}
            />
          ) : (
            <Editor
              height="100%"
              language="python"
              theme="lwt-instrument"
              beforeMount={defineEditorTheme}
              value={code}
              onChange={(value) => setCode(value ?? "")}
              onMount={handleMonacoMount}
              loading={<div style={{ ...s.small, padding: "14px" }}>Loading editor...</div>}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                fontFamily: "Geist Mono, JetBrains Mono, ui-monospace, Menlo, monospace",
                lineHeight: 21,
                tabSize: 4,
                insertSpaces: true,
                scrollBeyondLastLine: false,
                automaticLayout: true,
                wordWrap: "off",
                renderWhitespace: "none",
                padding: { top: 10, bottom: 10 },
              }}
            />
          )}
          </div>
          </section>
          <ResizeHandle
            orientation="horizontal"
            label="Resize code editor"
            value={sizes.editorH}
            min={140}
            max={1200}
            onChange={(v) => setSize("editorH", v)}
            onReset={() => setSize("editorH", PANEL_DEFAULTS.editorH)}
          />

          <section
            aria-label="Output"
            style={
              maximized === "output"
                ? fullscreenStyle
                : { height: sizes.outputH, flex: "0 0 auto", display: "flex", flexDirection: "column", minHeight: 0 }
            }
          >
          <div style={{ ...s.row, flexWrap: "nowrap", padding: "2px 8px 2px 16px", borderTop: `1px solid ${palette.border}`, background: palette.editor }}>
            <span style={{ ...s.small, flex: 1 }}>Output</span>
            {lines.length > 0 && (
              <button type="button" style={{ ...s.btnGhost, border: "none", minHeight: 32 }} onClick={() => setLines([])}>
                Clear
              </button>
            )}
            <FullscreenButton active={maximized === "output"} onToggle={() => toggleMax("output")} label="output" />
          </div>
          <pre style={{ ...s.output, borderTop: "none", flex: 1, minHeight: 0, maxHeight: "none" }}>
            {lines.length === 0 ? (
              <span style={{ color: palette.mutedText }}>Output appears here. Press Run to try the example.</span>
            ) : (
              lines.map((l, i) => (
                <div key={i} style={{ color: l.kind === "err" ? palette.danger : l.kind === "info" ? palette.mutedText : palette.text }}>
                  {l.text}
                </div>
              ))
            )}
          </pre>
          </section>
          <ResizeHandle
            orientation="horizontal"
            label="Resize output panel"
            value={sizes.outputH}
            min={100}
            max={800}
            onChange={(v) => setSize("outputH", v)}
            onReset={() => setSize("outputH", PANEL_DEFAULTS.outputH)}
          />

          <section
            aria-label="Chart"
            style={
              maximized === "chart"
                ? fullscreenStyle
                : { position: "relative", borderTop: `1px solid ${palette.border}`, flex: "0 0 auto" }
            }
          >
            <PythonChart
              candles={candles}
              output={chartOutput}
              height={maximized === "chart" ? "calc(100vh - 52px)" : sizes.chartH}
            />
            <div style={{ ...s.row, gap: "6px 14px", padding: "8px 8px 8px 16px" }}>
              <span style={{ fontSize: "0.75rem", fontWeight: 600 }}>{symbol.toUpperCase()} {tf}</span>
              {(chartOutput?.series || []).map((item, index) => (
                <span key={`${item.name}-${index}`} style={{ ...s.small, display: "inline-flex", alignItems: "center", gap: "6px", color: palette.text }}>
                  <span style={{ width: "12px", height: "2px", background: seriesColor(item, index), display: "inline-block" }} />
                  {item.name}
                  {item.pane === "oscillator" ? " (lower pane)" : ""}
                </span>
              ))}
              {chartOutput?.markers?.length > 0 && (
                <span style={s.small}>{chartOutput.markers.length} buy/sell arrows</span>
              )}
              {!chartOutput && (
                <span style={s.small}>
                  Your code draws here: <code>plot("name", values)</code> for lines, <code>signals(entries, exits)</code> for arrows.
                </span>
              )}
              <span style={{ flex: 1 }} />
              <FullscreenButton active={maximized === "chart"} onToggle={() => toggleMax("chart")} label="chart" />
            </div>
            {dataLoading && (
              <ChartLoadingOverlay
                symbol={symbol.toUpperCase()}
                timeframeLabel={tf}
                background={palette.background}
                gridColor={palette.border}
                textColor={palette.text}
                mutedColor={palette.mutedText}
              />
            )}
          </section>
          <ResizeHandle
            orientation="horizontal"
            label="Resize chart"
            value={sizes.chartH}
            min={180}
            max={1200}
            onChange={(v) => setSize("chartH", v)}
            onReset={() => setSize("chartH", PANEL_DEFAULTS.chartH)}
          />


        </main>

        {/* ---------------- right: AI helper ---------------- */}
        {!narrow && (
          <ResizeHandle
            orientation="vertical"
            label="Resize AI helper"
            value={sizes.asideW}
            min={260}
            max={720}
            invert
            onChange={(v) => setSize("asideW", v)}
            onReset={() => setSize("asideW", PANEL_DEFAULTS.asideW)}
          />
        )}
        <aside
          style={
            maximized === "ai"
              ? { ...s.aside, ...fullscreenStyle, borderLeft: "none" }
              : { ...s.aside, ...(narrow ? {} : { flex: `0 0 ${sizes.asideW}px` }) }
          }
          aria-label="AI helper"
        >
          <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${palette.border}`, paddingRight: 6 }}>
          <div style={{ flex: 1, minHeight: 48, display: "flex", alignItems: "center", paddingLeft: 16, fontSize: "0.85rem", fontWeight: 650, color: palette.text }}>Claude strategy assistant</div>
            <FullscreenButton active={maximized === "ai"} onToggle={() => toggleMax("ai")} label="AI helper" />
          </div>

          <div style={s.section}>
            <div style={s.small} role="status">
              {claudeConfigured === null ? "Checking Claude availability…" : claudeConfigured
                ? "Claude is ready. Provider credentials stay on the terminal server and are never sent to this browser."
                : "Claude is not configured on the terminal server yet. The site owner must add ANTHROPIC_API_KEY to the backend environment."}
            </div>
            <label style={{ ...s.small, display: "flex", gap: "8px", alignItems: "center" }}>
              <input type="checkbox" checked={includeCode} onChange={(e) => setIncludeCode(e.target.checked)} />
              Share the active Python file with Claude for this conversation
            </label>
            <label style={{ ...s.small, display: "flex", gap: "8px", alignItems: "center" }}>
              <input type="checkbox" checked={includeOutput} onChange={(e) => setIncludeOutput(e.target.checked)} />
              Share the latest run output or error
            </label>
            <label style={{ ...s.small, display: "flex", gap: "8px", alignItems: "center", opacity: backtestSummary ? 1 : 0.65 }}>
              <input type="checkbox" checked={includeBacktest} disabled={!backtestSummary} onChange={(e) => setIncludeBacktest(e.target.checked)} />
              Share the latest backtest summary{backtestSummary ? "" : " (run a backtest first)"}
            </label>
            <label style={{ ...s.small, display: "flex", gap: "8px", alignItems: "center" }}>
              <input type="checkbox" checked={includeData} onChange={(e) => setIncludeData(e.target.checked)} />
              Share the loaded candles with {aiName}
            </label>
          </div>

          <div style={s.section}>
            <label htmlFor="build-indicator" style={{ fontSize: "0.8125rem", fontWeight: 600 }}>Build an indicator</label>
            <div style={{ ...s.row, flexWrap: "nowrap" }}>
              <input
                id="build-indicator"
                style={{ ...s.field, flex: 1, minWidth: 0 }}
                value={buildText}
                onChange={(e) => setBuildText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    buildIndicator();
                  }
                }}
                placeholder="Describe it in your own words"
              />
              <button type="button" style={{ ...s.btn, ...(chatLoading ? pageStyles.buttonDisabled : {}) }} onClick={buildIndicator} disabled={chatLoading}>
                Build
              </button>
            </div>
          </div>

          <div style={s.chatLog}>
            {messages.length === 0 && (
              <div style={s.small}>
                Claude receives only the chart data, Python file, and run output you explicitly choose to share. Try: "Write a script that finds the 5 biggest candles".
              </div>
            )}
            {messages.map((m, i) =>
              m.role === "user" ? (
                <div key={i} style={s.bubbleUser}>{m.content}</div>
              ) : (
                <div key={i} style={s.bubbleAi}>
                  {splitReply(m.content).map((part, j) =>
                    part.kind === "text" ? (
                      <span key={j}>{part.value}</span>
                    ) : (
                      <div key={j}>
                        <pre style={s.codeBlock}>{part.value}</pre>
                        <div style={s.row}>
                          <button type="button" style={s.btnGhost} onClick={() => setCode(part.value + "\n")}>
                            Put in editor
                          </button>
                          <button
                            type="button"
                            style={s.btnGhost}
                            onClick={() => navigator.clipboard?.writeText(part.value).catch(() => {})}
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )
            )}
            {chatLoading && <div style={s.small}>{aiName} is thinking...</div>}
            {hasError && (
              <button
                type="button"
                style={{ ...s.btnGhost, alignSelf: "flex-start", minHeight: "40px", borderColor: palette.danger, color: palette.danger, fontWeight: 600 }}
                onClick={() => sendChat("My code gave an error. Please fix it and give me the complete corrected script.", { code: true, output: true })}
                disabled={chatLoading}
                title="This action sends the active Python file and latest run output to Claude."
              >
                Share code and error with {aiName} to fix
              </button>
            )}
            <div ref={chatEndRef} />
          </div>
          {chatError && <div style={{ ...pageStyles.errorBanner, margin: "0 16px 8px" }}>{chatError}</div>}

          <div style={{ ...s.row, padding: "0 16px 10px" }}>
            {[
              "Explain this code step by step",
              "Add a stop-loss and take-profit",
              "Which numbers should I try changing?",
              "Turn this indicator into a strategy",
              "Explain the backtest results",
            ].map((q) => (
              <button key={q} type="button" style={s.chip} onClick={() => sendChat(q)} disabled={chatLoading}>
                {q}
              </button>
            ))}
          </div>
          <div style={{ ...s.row, flexWrap: "nowrap", alignItems: "stretch", padding: "12px 16px", borderTop: `1px solid ${palette.border}` }}>
            <textarea
              style={{ ...s.field, flex: 1, minWidth: 0, minHeight: "44px", padding: "10px", resize: "vertical" }}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendChat();
                }
              }}
              placeholder={`Ask ${aiName} to write or fix Python`}
              aria-label={`Message to ${aiName}`}
            />
            <button type="button" style={{ ...s.btn, ...(chatLoading ? pageStyles.buttonDisabled : {}) }} onClick={sendChat} disabled={chatLoading}>
              Send
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
